import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, Stepper, Step, StepLabel,
  Button, Paper, TextField, CircularProgress, Snackbar, Alert,
  Autocomplete, IconButton, Tooltip
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

const steps = ['Editar información', 'Confirmación'];

const EditModal = ({ open, onClose, cfdiId, refreshTable }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [cfdiData, setCfdiData] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [ordenCompra, setOrdenCompra] = useState('');
  const [entradaMercancia, setEntradaMercancia] = useState('');
  const [documentoFinanciero, setDocumentoFinanciero] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true); // Iniciar en true para deshabilitar al abrir
  const [pdfValidating, setPdfValidating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [entradasOptions, setEntradasOptions] = useState([]);
  const [searchingEntradas, setSearchingEntradas] = useState(false);
  const [entradasError, setEntradasError] = useState('');
  const [formularioValido, setFormularioValido] = useState(false);

  useEffect(() => {
    if (open && cfdiId) {
      resetStates();
      fetchCfdiData();
    }
  }, [open, cfdiId]);

  const resetStates = () => {
    setActiveStep(0);
    setCfdiData(null);
    setPdfFile(null);
    setOrdenCompra('');
    setEntradaMercancia('');
    setDocumentoFinanciero('');
    setError('');
    setLoading(true);
    setPdfValidating(false);
    setSuccess(false);
    setEntradasOptions([]);
    setEntradasError('');
    setFormularioValido(false); // Siempre inicia en falso
  };

  const fetchCfdiData = async () => {
    try {
      const response = await axios.get(`https://facturas.verdevalle.com.mx/api/cfdi/${cfdiId}`, { 
        withCredentials: true 
      });
      setCfdiData(response.data);
      setOrdenCompra(response.data.ordendecompra || '');
      setEntradaMercancia(response.data.entradademercancia || '');
      setDocumentoFinanciero(response.data.documento_financiero || '');
      validarFormulario(response.data);
    } catch (error) {
      console.error('Error al obtener datos de la factura:', error);
      setError('Error al cargar datos de la factura');
    } finally {
      setLoading(false);
    }
  };

  const validarFormulario = (data) => {
    if (data?.TipoDeComprobante === 'P') {
      const valido = data.documento_financiero?.length === 10;
      setFormularioValido(valido);
    } else {
      const valido = !!data.entradademercancia;
      setFormularioValido(valido);
    }
  };

  const buscarEntradasMercancia = async () => {
    if (!ordenCompra || !cfdiData?.RfcEmisior) {
      setEntradasError('Se requiere orden de compra y RFC del emisor');
      setFormularioValido(false);
      return;
    }

    setSearchingEntradas(true);
    setEntradasError('');
    setEntradasOptions([]);
    setEntradaMercancia('');
    setFormularioValido(false);

    try {
      const response = await axios.get('https://facturas.verdevalle.com.mx/api/sap/pedidos', {
        withCredentials: true,
        params: {
          documento: ordenCompra,
          proveedor: cfdiData.RfcEmisior
        }
      });

      if (response.data.length === 0) {
        setEntradasError('No se encontraron entradas para esta orden de compra');
        return;
      }

      const opciones = response.data.map(item => ({
        label: `${item.Entrada} - ${item.Desmaterial} - ${item.Cantidad} (${item.Importe} ${item.Moneda})`,
        value: item.Entrada,
        material: item.Material,
        cantidad: item.Cantidad
      }));

      setEntradasOptions(opciones);

      if (opciones.length === 1) {
        setEntradaMercancia(opciones[0].value);
        setFormularioValido(true);
      }
    } catch (err) {
      console.error('Error al buscar entradas:', err);
      setEntradasError(err.response?.data?.error || 'Error al consultar entradas de mercancía');
    } finally {
      setSearchingEntradas(false);
    }
  };

  const handleDocumentoFinancieroChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setDocumentoFinanciero(value);
    setFormularioValido(value.length === 10);
  };

  const handleEntradaMercanciaChange = (_, newValue) => {
    const value = newValue?.value || '';
    setEntradaMercancia(value);
    setFormularioValido(!!value);
  };

  const getCfdiUuid = () => cfdiData?.uuid || cfdiData?.UUID || cfdiData?.Uuid || '';

  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setPdfFile(null);
    setError('');

    const cfdiUuid = getCfdiUuid();
    if (!cfdiUuid && !cfdiId) {
      setError('No se pudo validar el PDF: la factura no tiene UUID');
      event.target.value = '';
      return;
    }

    setPdfValidating(true);

    try {
      const formData = new FormData();
      formData.append('pdfFile', file);
      formData.append('uuid', cfdiUuid);
      formData.append('cfdiId', cfdiId);

      await axios.post('https://facturas.verdevalle.com.mx/api/validate-pdf-uuid', formData, {
        withCredentials: true
      });

      setPdfFile(file);
    } catch (err) {
      const apiError = err.response?.data?.validation || err.response?.data;
      setError(apiError?.error || apiError?.message || 'El PDF adjunto no coincide con la factura');
      event.target.value = '';
    } finally {
      setPdfValidating(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      formData.append('ordenCompra', ordenCompra);
      formData.append('entradaMercancia', entradaMercancia);
      formData.append('documentoFinanciero', documentoFinanciero);
      if (pdfFile) {
        formData.append('pdfFile', pdfFile);
      }

      const response = await axios.put(
        `https://facturas.verdevalle.com.mx/api/cfdi/${cfdiId}`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        if (refreshTable) {
          await refreshTable();
        }
        setSuccess(true);
        setActiveStep(1);
      } else {
        setError(response.data.error || 'Error al actualizar la factura');
      }
    } catch (error) {
      console.error('Error al actualizar factura:', error);
      const apiError = error.response?.data?.validation || error.response?.data;
      if (apiError?.status === 'PDF_UUID_MISMATCH' || /PDF adjunto/i.test(apiError?.message || apiError?.error || '')) {
        setError('El PDF adjunto no coincide con la factura');
      } else {
        setError(apiError?.error || 'Error al actualizar la factura');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizar = () => {
    onClose();
  };

  const handleCancelar = () => {
    if (!loading && !pdfValidating) onClose();
  };

  return (
    <Modal open={open} onClose={handleCancelar}>
      <Paper sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxWidth: '95%',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <Typography variant="h6" gutterBottom>Editar factura</Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>Editar información</Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography gutterBottom>
                    Actualizar PDF del documento
                  </Typography>
                  <input
                    accept=".pdf"
                    id="pdf-upload"
                    type="file"
                    hidden
                    onChange={handlePdfUpload}
                    disabled={loading || pdfValidating}
                  />
                  <label htmlFor="pdf-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PictureAsPdfIcon />}
                      fullWidth
                      disabled={loading || pdfValidating}
                      sx={{
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        paddingRight: 1
                      }}
                    >
                      {pdfValidating
                        ? 'Validando PDF...'
                        : (pdfFile ? pdfFile.name : (cfdiData?.rutadealmacenamientopdf ? 'Actualizar PDF' : 'Agregar PDF'))}
                    </Button>
                  </label>
                </Box>

                {cfdiData?.TipoDeComprobante === 'P' ? (
                  <Box>
                    <TextField
                      label="Documento financiero *"
                      variant="outlined"
                      fullWidth
                      value={documentoFinanciero}
                      onChange={handleDocumentoFinancieroChange}
                      sx={{ mb: 2 }}
                      disabled={loading}
                      error={documentoFinanciero.length > 0 && documentoFinanciero.length !== 10}
                      helperText={
                        documentoFinanciero.length !== 10
                          ? 'El documento financiero debe tener exactamente 10 dígitos numéricos'
                          : 'Documento financiero válido'
                      }
                    />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <TextField
                        label="Orden de compra *"
                        variant="outlined"
                        fullWidth
                        value={ordenCompra}
                        onChange={(e) => setOrdenCompra(e.target.value)}
                        disabled={loading}
                      />
                      <Tooltip title="Buscar entradas de mercancía">
                        <span>
                          <IconButton
                            onClick={buscarEntradasMercancia}
                            disabled={!ordenCompra || !cfdiData?.RfcEmisior || searchingEntradas || loading}
                            color="primary"
                          >
                            {searchingEntradas ? <CircularProgress size={24} /> : <SearchIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <Autocomplete
                      options={entradasOptions}
                      getOptionLabel={(option) => option?.label || ''}
                      value={entradasOptions.find(opt => opt.value === entradaMercancia) || null}
                      onChange={handleEntradaMercanciaChange}
                      disabled={entradasOptions.length === 0 || searchingEntradas || loading}
                      loading={searchingEntradas}
                      sx={{ mb: 2 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Entrada de mercancía *"
                          variant="outlined"
                          fullWidth
                          error={!!entradasError}
                          helperText={entradasError || (entradaMercancia ? "Entrada seleccionada" : "Seleccione una entrada para habilitar actualización")}
                        />
                      )}
                    />
                  </>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button
                    onClick={handleCancelar}
                    disabled={loading}
                    variant="outlined"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleUpdate}
                    disabled={!formularioValido || loading || pdfValidating}
                    sx={{
                      backgroundColor: (!formularioValido || loading || pdfValidating) ? '#f5f5f5' : 'primary.main',
                      color: (!formularioValido || loading || pdfValidating) ? '#bdbdbd' : 'white',
                      '&:hover': {
                        backgroundColor: (!formularioValido || loading || pdfValidating) ? '#f5f5f5' : 'primary.dark'
                      }
                    }}
                  >
                    {pdfValidating ? 'Validando PDF...' : (loading ? 'Actualizando...' : 'Actualizar')}
                    {(loading || pdfValidating) && <CircularProgress size={24} sx={{ ml: 1 }} />}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Factura actualizada exitosamente
            </Typography>
            <Button
              variant="contained"
              onClick={handleFinalizar}
              sx={{ mt: 3 }}
            >
              Finalizar
            </Button>
          </Box>
        )}

        {error && (
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}
      </Paper>
    </Modal>
  );
};

export default EditModal;
